import React, { useState, useRef, useContext, useEffect } from "react";
import { updateDoc, doc, getDoc, arrayRemove } from "firebase/firestore";
import { arrayMoveImmutable } from "array-move";
import { SortableContainer, SortableElement } from "react-sortable-hoc";
import { toast } from "react-toastify";
import { EnvContext } from "../../../context/EnvContext";
import { useFirestore } from "../../firebase/FirestoreContext";
import { useParams, useNavigate } from "react-router-dom";
import config from "../../../config";

// The sortable image element for sort mode
const SortableImage = SortableElement(({ image, index }) => (
  <div style={styles.imageContainer}>
    <div style={styles.imageWrapper}>
      <img src={image} alt={`product-${index}`} style={styles.image} />
    </div>
  </div>
));

// The sortable list container
const SortableImageList = SortableContainer(({ images }) => (
  <div style={styles.imageList}>{images && images.map((image, index) => <SortableImage key={`${image}-${index}`} index={index} image={image} />)}</div>
));

// A simple non-draggable component for delete mode
const DeletableImage = ({ image, onDelete }) => (
  <div style={styles.imageContainer}>
    <div style={styles.imageWrapper}>
      <img src={image} alt="product" style={styles.image} />
    </div>
    <button
      type="button" // Prevents form submission
      style={styles.deleteButton}
      onClick={() => {
        console.log("Delete clicked for", image);
        onDelete(image);
      }}
    >
      Delete
    </button>
  </div>
);

// A list that maps over images and renders a DeletableImage
const DeletableImageList = ({ images, onDelete }) => (
  <div style={styles.imageList}>{images && images.map((image, index) => <DeletableImage key={`${image}-${index}`} image={image} onDelete={onDelete} />)}</div>
);

const ImageUpload = () => {
  const environment = useContext(EnvContext);
  const db = useFirestore();
  const { id } = useParams();
  const navigate = useNavigate();

  const collectionPath = environment === "staging" ? config.enveironment.staging.collectionPath : config.enveironment.production.collectionPath;

  const [files, setFiles] = useState([]);
  const fileInputRef = useRef(null);
  const [productData, setProductData] = useState({ images: [] });
  const [loading, setLoading] = useState(false);
  // mode can be "sort" or "delete"
  const [mode, setMode] = useState("sort");

  const fetchProductData = async () => {
    if (id) {
      const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);
      const docSnap = await getDoc(productDocRef);
      if (docSnap.exists()) {
        setProductData(docSnap.data());
      } else {
        console.log("No such document!");
      }
    }
  };

  useEffect(() => {
    fetchProductData();
  }, [db, collectionPath, id]);

  const handleFileChange = (e) => {
    setFiles([...e.target.files]);
  };

  const handleImageUpload = async (event) => {
    event.preventDefault();
    setLoading(true);

    let updatedImages = [...(productData.images || [])];

    for (const file of files) {
      const formData = new FormData();
      formData.append("file", file);

      try {
        const apiUrl = `${config.apiConfig.API_ENDPOINT_URL}${environment === "staging" ? "/api/dev" : "/api"}/product/productImages/${id}`;

        const response = await fetch(apiUrl, {
          method: "POST",
          body: formData,
        });

        if (!response.ok) {
          const errorBody = await response.text();
          throw new Error(`HTTP error! status: ${response.status}, body: ${errorBody}`);
        }

        const result = await response.json();

        if (result && Array.isArray(result.imageUrls)) {
          for (const imageUrl of result.imageUrls) {
            updatedImages.push(imageUrl);
          }
        } else {
          throw new Error(`Failed to upload: ${file.name}, invalid server response: ${JSON.stringify(result)}`);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
        alert(`Error uploading image - ${error.message}`);
      }
    }
    await fetchProductData();
    fileInputRef.current.value = "";

    setLoading(false);
  };

  const deleteImage = async (imageUrl) => {
    setLoading(true);
    try {
      const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);

      // Use arrayRemove to atomically remove the image from the Firestore array
      await updateDoc(productDocRef, { images: arrayRemove(imageUrl) });
      await fetchProductData();
      toast.success("Image deleted successfully");
    } catch (error) {
      console.error("Error deleting image:", error);
      toast.error("Error deleting image");
    } finally {
      setLoading(false);
    }
  };

  // Called only in sort mode
  const onSortEnd = async ({ oldIndex, newIndex }) => {
    const newImages = arrayMoveImmutable(productData.images, oldIndex, newIndex);
    setProductData((prev) => ({ ...prev, images: newImages }));
    const productDocRef = doc(db, `${collectionPath}Products/ProductsList/DataBase`, id);
    try {
      await updateDoc(productDocRef, { images: newImages });
      toast.success("Image order updated successfully");
    } catch (error) {
      console.error("Error updating image order:", error);
      toast.error("Failed to update image order");
    }
  };

  return (
    <form onSubmit={handleImageUpload} style={styles.container}>
      {/* Mode toggle buttons */}
      <div style={styles.modeToggleContainer}>
        <button
          type="button"
          onClick={() => setMode("sort")}
          style={{
            ...styles.modeToggleButton,
            backgroundColor: mode === "sort" ? "white" : "transparent",
            border: mode === "sort" ? "1px solid black" : "1px solid #ccc",
            color: mode === "sort" ? "black" : "black",
          }}
        >
          Sort Images
        </button>
        <button
          type="button"
          onClick={() => setMode("delete")}
          style={{
            ...styles.modeToggleButton,
            backgroundColor: mode === "delete" ? "white" : "white",
            border: mode === "delete" ? "1px solid black" : "1px solid #ccc",
            color: mode === "delete" ? "black" : "black",
          }}
        >
          Delete Images
        </button>
      </div>
      <div style={styles.uploadSection}>
        {mode === "sort" ? (
          <SortableImageList images={productData.images} onSortEnd={onSortEnd} axis="xy" />
        ) : (
          <DeletableImageList images={productData.images} onDelete={deleteImage} />
        )}
      </div>
      <div style={styles.formElement}>
        <label style={styles.formLabel}>Upload Image:</label>
        <input ref={fileInputRef} type="file" onChange={handleFileChange} accept="image/*" multiple style={styles.formInput} />
      </div>
      <button type="submit" style={{ ...styles.button, backgroundColor: loading ? "silver" : "#478169" }} disabled={loading}>
        {loading ? "Uploading..." : "Upload Image"}
      </button>
    </form>
  );
};

const styles = {
  container: {
    padding: 20,
    display: "flex",
    flexDirection: "column",
    gap: 10,
    border: "1px solid #ccc",
    borderRadius: 5,
    backgroundColor: "#f9f9f9",
    width: "100%",
  },
  modeToggleContainer: {
    display: "flex",
    gap: 10,
    marginBottom: 10,
  },
  modeToggleButton: {
    flex: 1,
    padding: "8px 12px",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 14,
  },
  uploadSection: {
    marginBottom: 50,
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  formElement: {
    marginBottom: 10,
  },
  formLabel: {
    marginBottom: 5,
    fontSize: 12,
    textTransform: "uppercase",
    color: "#4a4141",
  },
  formInput: {
    padding: "10px 15px",
    fontSize: 16,
    border: "0.5px solid #ccc",
    borderRadius: 5,
    width: "100%",
  },
  button: {
    padding: "0px",
    width: "120px",
    color: "white",
    border: "none",
    borderRadius: 5,
    cursor: "pointer",
    fontSize: 14,
    height: 50,
  },
  imageList: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
  },
  imageContainer: {
    marginBottom: 15,
    marginRight: 10,
    position: "relative",
  },
  imageWrapper: {
    display: "flex",
    alignItems: "center",
    marginBottom: 10,
    padding: 2,
    border: "1px solid silver",
  },
  image: {
    width: 150,
    height: 150,
    objectFit: "cover",
  },
  deleteButton: {
    fontSize: 12,
    color: "red",
    cursor: "pointer",
    top: 0,
    right: 0,
    padding: "2px 5px",
    borderRadius: "3px",
    zIndex: 2,
    border: "none",
    background: "transparent",
  },
};

export default ImageUpload;
